import { useState, useEffect, useCallback } from 'react';
import { 
  getUserStreak, 
  updateUserActivity, 
  getStreakStats,
  StreakData,
  StreakStats,
  UpdateActivityResponse 
} from '../api/streakService';

export const useStreak = () => {
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [streakStats, setStreakStats] = useState<StreakStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStreakData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserStreak();
      setStreakData(data);
    } catch (err) {
      console.error('Error fetching streak data:', err);
      setError('Failed to load streak data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStreakStats = useCallback(async () => {
    try {
      setError(null);
      const data = await getStreakStats();
      setStreakStats(data);
    } catch (err) {
      console.error('Error fetching streak stats:', err);
      setError('Failed to load streak statistics');
    }
  }, []);

  const updateActivity = useCallback(async (): Promise<UpdateActivityResponse | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await updateUserActivity();
      
      if (response.streakUpdated) {
        // Refresh streak data after successful update
        await fetchStreakData();
        await fetchStreakStats();
      }
      
      return response;
    } catch (err) {
      console.error('Error updating activity:', err);
      setError('Failed to update activity');
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchStreakData, fetchStreakStats]);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchStreakData(), fetchStreakStats()]);
  }, [fetchStreakData, fetchStreakStats]);

  useEffect(() => {
    fetchStreakData();
  }, [fetchStreakData]);

  return {
    streakData,
    streakStats,
    loading,
    error,
    updateActivity,
    refreshData,
    fetchStreakData,
    fetchStreakStats,
  };
};
